// Frontend Logger Utility - Environment-aware logging for React/Browser
class Logger {
  constructor() {
    // Detect environment from various sources
    this.hostname = typeof window !== 'undefined' ? window.location.hostname : 'unknown';
    this.isLocalhost = this.hostname === 'localhost' || this.hostname === '127.0.0.1';
    this.isStaging = this.hostname === 'staging.smushlabs.ai'; // Exact match only
    this.isProduction = this.hostname === 'www.smushlabs.ai' || this.hostname === 'smushlabs.ai';
    this.isDevelopment = this.isLocalhost || this.isStaging;

    // Check for debug parameter in URL
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      this.forceDebug = urlParams.get('debug') === 'true' || urlParams.get('logger_debug') === 'true' || urlParams.get('chatbot_debug') === 'true';

      // Check if this is an embedded widget (script loaded from smushlabs.ai)
      // This enables verbose error logging for debugging widget issues on third-party sites
      this.isEmbeddedWidget = this.detectEmbeddedWidget();
    } else {
      this.forceDebug = false;
      this.isEmbeddedWidget = false;
    }

    // Check NODE_ENV for build-time environment detection
    this.nodeEnvDev = process.env.NODE_ENV === 'development';

    // Enable logging in development environments or when debug is forced
    // Note: Disabled for embedded widgets on third-party sites to keep client console clean
    this.shouldLog = this.isDevelopment || this.forceDebug || this.nodeEnvDev;
  }

  // Detect if running as an embedded widget on a third-party site
  detectEmbeddedWidget() {
    if (typeof document === 'undefined') return false;

    const scripts = document.getElementsByTagName('script');
    for (let i = 0; i < scripts.length; i++) {
      const src = scripts[i].src;
      if (src && (src.includes('smushlabs.ai') && (src.includes('chatbot-loader.js') || src.includes('smushchat-widget.js')))) {
        // Widget is loaded from smushlabs.ai on a third-party domain
        return !this.isLocalhost && !this.isStaging && !this.isProduction;
      }
    }
    return false;
  }


  log(...args) {
    if (this.shouldLog) {
      console.log(...args);
    }
  }

  info(...args) {
    if (this.shouldLog) {
      console.info(`[${new Date().toISOString()}] INFO:`, ...args);
    }
  }

  warn(...args) {
    if (this.shouldLog) {
      console.warn(`[${new Date().toISOString()}] WARN:`, ...args);
    }
  }

  error(...args) {
    if (this.shouldLog) {
      console.error(`[${new Date().toISOString()}] ERROR:`, ...args);
    } else {
      // In production, log minimal error info for security
      console.error('Error occurred');
    }
  }

  debug(...args) {
    if (this.shouldLog) {
      console.debug(`[${new Date().toISOString()}] DEBUG:`, ...args);
    }
  }

  success(...args) {
    if (this.shouldLog) {
      console.log(`[${new Date().toISOString()}] ✅ SUCCESS:`, ...args);
    }
  }

  critical(...args) {
    // Critical errors are always logged, even in production
    console.error(`[${new Date().toISOString()}] 🚨 CRITICAL:`, ...args);
  }

  table(data) {
    if (this.shouldLog && console.table) {
      console.table(data);
    }
  }

  group(label) {
    if (this.shouldLog && console.group) {
      console.group(label);
    }
  }

  groupEnd() {
    if (this.shouldLog && console.groupEnd) {
      console.groupEnd();
    }
  }

  time(label) {
    if (this.shouldLog && console.time) {
      console.time(label);
    }
  }

  timeEnd(label) {
    if (this.shouldLog && console.timeEnd) {
      console.timeEnd(label);
    }
  }

  // Environment-specific logging
  devOnly(...args) {
    if (this.isLocalhost) {
      console.log('[DEV ONLY]', ...args);
    }
  }

  stagingOnly(...args) {
    if (this.isStaging) {
      console.log('[STAGING ONLY]', ...args);
    }
  }

  localOnly(...args) {
    if (this.isLocalhost) {
      console.log('[LOCAL ONLY]', ...args);
    }
  }

  getEnvironment() {
    return {
      hostname: this.hostname,
      isLocalhost: this.isLocalhost,
      isStaging: this.isStaging,
      isProduction: this.isProduction,
      isDevelopment: this.isDevelopment,
      forceDebug: this.forceDebug,
      nodeEnvDev: this.nodeEnvDev,
      shouldLog: this.shouldLog,
      nodeEnv: process.env.NODE_ENV,
      loggingEnabled: this.shouldLog
    };
  }
}

const logger = new Logger();

// Log initialization info in development only
if (logger.shouldLog) {
  logger.debug('🔧 Frontend logger initialized', logger.getEnvironment());
}

export default logger;
